/**
 * MCP Server Hooks
 *
 * React Query hooks for fetching MCP servers and tools from Azure APIs.
 * Follows Otto patterns with caching and automatic token management.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import {
  getMcpServers,
  getMcpToolsByConnector,
  listMcpTools,
  categorizeMcpServers,
  type McpServerOperation,
  type McpToolOperation,
  type McpToolFromBackend,
  type McpConnectionInfo,
} from '@/services/mcpApi';
import { mcpConnectionToServer } from '@/services/mcpHelpers';
import { type McpConnection } from '@/types/logicApps';
import { useCurrentProject } from './useCurrentProject';
import { useConnectionsJson, useConnectionsDraftJson } from './useConnections';

// =============================================================================
// QUERY KEYS
// =============================================================================

/**
 * Build a stable fingerprint for an MCP connection so cache keys can
 * differentiate two connections that share the same id but have different
 * endpoints or auth (e.g. a draft override of a saved BYO connection). We
 * intentionally include the auth `type` but NOT secrets so the fingerprint
 * stays safe to embed in React Query devtools / logs.
 */
export function connectionFingerprint(connection: McpConnectionInfo | undefined): string {
  if (!connection) return '';
  // Native connections carry mcpServerUrl; managed connections carry
  // connectionRuntimeUrl. Include both so two managed references resolving
  // to different runtime URLs never collide in the cache.
  const url = connection.mcpServerUrl ?? connection.connectionRuntimeUrl ?? '';
  const auth = connection.authentication as Record<string, unknown> | undefined;
  const authType = typeof auth?.type === 'string' ? auth.type : auth ? 'present' : 'none';
  return `${url}|${authType}`;
}

export const mcpKeys = {
  all: ['mcp'] as const,
  servers: (projectName: string) =>
    [...mcpKeys.all, 'servers', projectName] as const,
  tools: (projectName: string, connectorId: string) =>
    [...mcpKeys.all, 'tools', projectName, connectorId] as const,
  connectionTools: (
    projectName: string,
    appName: string,
    workflowName: string,
    connectionId: string,
    fingerprint: string,
    mcpServerPath?: string,
  ) =>
    [
      ...mcpKeys.all,
      'connectionTools',
      projectName,
      appName,
      workflowName,
      connectionId,
      fingerprint,
      mcpServerPath,
    ] as const,
};

// =============================================================================
// CACHE CONFIGURATION
// =============================================================================

// MCP servers don't change frequently - use longer cache
const CACHE_CONFIG = {
  staleTime: 1000 * 60 * 60, // 1 hour
  gcTime: 1000 * 60 * 60 * 2, // 2 hours
  retry: 1,
};

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Categorized MCP servers result
 */
export interface CategorizedMcpServers {
  /** Microsoft-managed shared servers */
  shared: McpServerOperation[];
  /** BYO MCP servers persisted in connections.json (agentMcpConnections map) */
  others: McpServerOperation[];
}

/**
 * Fetch all available MCP servers for the current app's subscription and location
 *
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with categorized MCP servers
 */
export function useMcpServersQuery(
  enabled = true
): UseQueryResult<CategorizedMcpServers, Error> {
  const { getOttoToken } = useAuth();
  const selectedApp = useAppStore((state) => state.selectedApp);

  const projectName = selectedApp?.projectName ?? '';

  return useQuery({
    queryKey: mcpKeys.servers(projectName),
    queryFn: async () => {
      if (!projectName) {
        throw new Error('No app selected - project name required');
      }

      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to get access token');
      }

      const servers = await getMcpServers({
        projectName,
        accessToken: token,
      });

      const { shared } = categorizeMcpServers(servers);

      return {
        shared,
        // `others` is populated by `useMcpConnectionsForApp` (a separate
        // query against connections.json). Keeping the sources independent
        // lets the picker show partial successes if one source fails.
        others: [] as McpServerOperation[],
      };
    },
    enabled: enabled && !!projectName,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch tools available from a specific MCP server/connector
 *
 * @param connectorId - Full connector ID (e.g., /subscriptions/.../managedApis/mcpServer)
 * @param enabled - Whether to enable the query (default: true when connectorId provided)
 * @returns Query result with MCP tools
 */
export function useMcpToolsQuery(
  connectorId: string | undefined,
  enabled = true
): UseQueryResult<McpToolOperation[], Error> {
  const { getOttoToken } = useAuth();
  const selectedApp = useAppStore((state) => state.selectedApp);

  const projectName = selectedApp?.projectName ?? '';

  return useQuery({
    queryKey: mcpKeys.tools(projectName, connectorId ?? ''),
    queryFn: async () => {
      if (!connectorId) {
        throw new Error('No connector ID provided');
      }

      if (!projectName) {
        throw new Error('No app selected - project name required');
      }

      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to get access token');
      }

      const tools = await getMcpToolsByConnector(
        {
          projectName,
          accessToken: token,
        },
        connectorId
      );

      return tools;
    },
    enabled: enabled && !!connectorId && !!projectName,
    ...CACHE_CONFIG,
  });
}

/**
 * Combined hook that provides both servers and tools state management
 * Useful for the MCP picker panel flow
 */
export function useMcpPicker() {
  const serversQuery = useMcpServersQuery();

  return {
    // Server data
    servers: serversQuery.data,
    isLoadingServers: serversQuery.isLoading,
    serversError: serversQuery.error,
    refetchServers: serversQuery.refetch,

    // Tools hook factory - call with selected server's connector ID
    useToolsForServer: (connectorId: string | undefined) =>
      useMcpToolsQuery(connectorId),
  };
}

/**
 * Fetch tools for a specific MCP connection using the backend listMcpTools API
 *
 * This hook uses the Logic Apps runtime to dynamically discover tools
 * from an MCP server connection, which provides accurate tool discovery
 * based on the actual MCP server configuration.
 *
 * @param connection - Connection info with ID and optional properties
 * @param workflowName - The workflow name for the API call
 * @param mcpServerPath - Optional server path (e.g., for BYO MCP servers)
 * @param enabled - Whether to enable the query (default: true when connection provided)
 * @returns Query result with MCP tools from the connection
 *
 * @example
 * ```tsx
 * function McpToolsList({ connectionId }: { connectionId: string }) {
 *   const { data: tools, isLoading } = useMcpToolsForConnection(
 *     { id: connectionId },
 *     'myWorkflow'
 *   );
 *   if (isLoading) return <Spinner />;
 *   return <ul>{tools?.map(t => <li key={t.name}>{t.name}</li>)}</ul>;
 * }
 * ```
 */
export function useMcpToolsForConnection(
  connection: McpConnectionInfo | undefined,
  workflowName: string | undefined,
  mcpServerPath?: string,
  enabled = true
): UseQueryResult<McpToolFromBackend[], Error> {
  const { getOttoToken } = useAuth();
  const selectedApp = useAppStore((state) => state.selectedApp);
  const { projectName } = useCurrentProject();

  // The Otto application name (`selectedApp.name`) is the canonical segment
  // used by `/automationProjects/{p}/applications/{appName}/...` URLs — same
  // pattern every other Otto hook (`useSaveWorkflow`, `useDraftSave`,
  // `useConnectionMutations`, etc.) uses. Do NOT derive this from the ACA
  // container app ARM id via `parseAppId(...).appName` — that returns the
  // underlying ACA resource name which is unrelated to the Otto app name and
  // produces 403s on VFS calls.
  const appName = selectedApp?.name ?? '';

  return useQuery({
    queryKey: mcpKeys.connectionTools(
      projectName ?? '',
      appName,
      workflowName ?? '',
      connection?.id ?? '',
      connectionFingerprint(connection),
      mcpServerPath,
    ),
    queryFn: async () => {
      if (!connection?.id) {
        throw new Error('No connection provided');
      }

      if (!workflowName) {
        throw new Error('No workflow name provided');
      }

      if (!appName) {
        throw new Error('No app selected');
      }

      if (!projectName) {
        throw new Error('No project selected');
      }

      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to get Otto access token');
      }

      const tools = await listMcpTools(
        {
          projectName,
          appName,
          workflowName,
          accessToken: token,
        },
        connection,
        mcpServerPath
      );

      return tools;
    },
    enabled: enabled && !!connection?.id && !!workflowName && !!appName && !!projectName,
    ...CACHE_CONFIG,
    // 403 from VFS RBAC is a deterministic backend permission issue; retrying
    // it just delays the inline error surface for users without the
    // `automationProjects/applications/vfs/read` permission on this app.
    // Must come after `...CACHE_CONFIG` to override its `retry: 1`.
    retry: false,
  });
}

// =============================================================================
// BYO MCP CONNECTIONS (from connections.json.agentMcpConnections)
// =============================================================================

/**
 * Result of `useMcpConnectionsForApp` — BYO MCP servers derived from the
 * current app's connections.json (and its draft variant when present).
 */
export interface McpConnectionsResult {
  /** BYO MCP servers, one card per `agentMcpConnections[<key>]` entry. */
  servers: McpServerOperation[];
  /** Underlying records keyed by their connection key — useful for tool fetch. */
  records: Record<string, McpConnection>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Derive BYO MCP servers from the current app's `connections.json` (plus the
 * draft variant when the workflow is being edited). Each entry under
 * `agentMcpConnections` becomes a `McpServerOperation` card consumed by the
 * picker's "Others" tab.
 *
 * Draft entries take precedence over saved ones with the same key, mirroring
 * the editor convention in `useConnectionsEditor`.
 */
export function useMcpConnectionsForApp(
  options: { enabled?: boolean } = {},
): McpConnectionsResult {
  const enabled = options.enabled ?? true;
  const selectedApp = useAppStore((state) => state.selectedApp);
  const { projectName } = useCurrentProject();

  // `selectedApp.name` is the canonical Otto application name; see the
  // comment in `useMcpToolsForConnection` for why we don't parse ARM ids.
  const appName = selectedApp?.name ?? '';
  const appId = selectedApp?.armResourceId ?? selectedApp?.properties?.appResourceId ?? '';

  const info = projectName && appName
    ? { projectName, appName, appId }
    : null;

  // When the caller has no use for these (e.g. a managed-MCP tool whose
  // records are never read), short-circuit the underlying React Query
  // subscriptions by passing `null` info — the existing no-op path in
  // `useConnections`. This avoids needless refetches and re-renders while
  // keeping hook order unconditional.
  const queryInfo = enabled ? info : null;

  const savedQuery = useConnectionsJson(queryInfo);
  const draftQuery = useConnectionsDraftJson(queryInfo);

  return useMemo(() => {
    // Records come from the DRAFT snapshot when it carries an explicit
    // `agentMcpConnections` bucket; otherwise we fall back to the saved
    // connections.json bucket.
    //
    // Why the draft takes precedence: a BYO deletion in the current draft
    // always rewrites the draft bucket in place (keeping it PRESENT — it
    // becomes `{}` when the last record is removed — and writing both files),
    // so reading the draft bucket honors draft deletions without merging saved
    // entries back in (which would resurrect a just-deleted server).
    //
    // Why we fall back to saved on bucket ABSENCE: a draft file can exist
    // without ever having had an MCP override (e.g. the user added a managed
    // API connection in the draft). Absence means "no draft MCP override," not
    // "delete all MCP connections," so the saved bucket is the right source.
    // We only fall back once `draftQuery.data` has resolved so the loading
    // state never briefly surfaces saved records the draft will remove.
    //
    // We filter records lacking `mcpServerUrl` so neither `servers` nor
    // `records` ever contain malformed entries that would crash downstream
    // renderers (e.g. `record.mcpServerUrl.toLowerCase()`).
    const sanitize = (
      raw: Record<string, McpConnection> | undefined,
    ): Record<string, McpConnection> => {
      const out: Record<string, McpConnection> = {};
      for (const [key, value] of Object.entries(raw ?? {})) {
        if (value && typeof value.mcpServerUrl === 'string' && value.mcpServerUrl.length > 0) {
          out[key] = value;
        }
      }
      return out;
    };

    const draftData = draftQuery.data;
    const draftHasMcpBucket =
      !!draftData &&
      Object.prototype.hasOwnProperty.call(draftData, 'agentMcpConnections');
    const rawRecords = draftHasMcpBucket
      ? draftData.agentMcpConnections
      : draftData
        ? savedQuery.data?.agentMcpConnections
        : undefined;

    const records: Record<string, McpConnection> = sanitize(rawRecords);

    const servers = Object.entries(records).map(([key, record]) =>
      mcpConnectionToServer(record, key),
    );

    return {
      servers,
      records,
      isLoading: savedQuery.isLoading || draftQuery.isLoading,
      isError: savedQuery.isError || draftQuery.isError,
      error: (savedQuery.error as Error | null) ?? (draftQuery.error as Error | null) ?? null,
    };
  }, [draftQuery.data, savedQuery.data, savedQuery.isLoading, draftQuery.isLoading,
      savedQuery.isError, draftQuery.isError, savedQuery.error, draftQuery.error]);
}
